import React from "react";
import { useDataLoader } from "react-remote-data-hooks";
import api from "../../api";
import { ContainerLoader } from "../../components/loader";
import { AppPermissions, Settings, useHasPermission } from "../../core";
import AppLayout from "../../core/layouts/AppLayout";
import "../../styles/listing.scss";
import "../../styles/settings.scss";
import GeneralSettingsTab from "./tabs/general-settings";

import LeftMenu from "./components/left-menu";
import { SettingsTabNames } from "./props";
import SMSSettingsTab from "./tabs/sms-settings";
import AutolineSettingsTab from "./tabs/autoline-settings";
import AzureAdSettingsTab from "./tabs/azure-ad-settings";
import OutlookSettingsTab from "./tabs/outlook-settings";
import EmailSettingsTab from "./tabs/email-settings";
import OorjitSettingsTab from "./tabs/oorjit-settings";
import FacebookLeadsApiSettingsTab from "./tabs/facebook-leads-api-settings";
import TiktokokLeadsApiSettingsTab from "./tabs/tiktok-leads-settings";
import LinkedInLeadsApiSettingsTab from "./tabs/linked-in-leads-api-settings";
import QuotationSettingsTab from "./tabs/quotation-settings";
import BookingSettingsTab from "./tabs/booking-settings";
import LeadAssignmentTab from "./tabs/lead-assignment";
import SiblingBrandsSettingsTab from "./tabs/sibling-brands-settings";
import OneDriveSettingsTab from "./tabs/one-drive-settings";
import LeadActivitySettingsTab from "./tabs/lead-activity";
import ShortenUrlSettingsTab from "./tabs/shorten-url-settings";
import PDFSettingsTab from "./tabs/pdf-settings";
import ApplicationSettingsTab from "./tabs/application-settings";
import NotificationSettingsTab from "./tabs/notification-settings";
import SocialMediaLinksTab from "./tabs/social-media-links";
import LeadLostApprovalSettingsTab from "./tabs/lead-lost-approval-settings";
import AiApiSettingsTab from "./tabs/ai-api-settings";
import FollowUpGapTimeSettingsTab from "./tabs/follow-up-time-settings";
import FollowupSettingsTab from "./tabs/followup-settings";
import AppNotificationSettingsTab from "./tabs/app-notification-settings";
import StageToMustFieldMappingTab from "./tabs/stage-to-must-field-mapping";
import PreBookingSettingsTab from "./tabs/pre-booking-settings";
import VehicleReportSettingsTab from "./tabs/vehicle-report-settings";
import HolidaySettingsTab from "./tabs/holiday-settings";
import ActivitySettingsTab from "./tabs/activity-settings";
import UserbackSettingsTab from "./tabs/userback-settings";
import CheckListManagement from "./tabs/checklist-settings";
import WhatsappLeadAssignmentTab from "./tabs/lead-assignment-wapp";
import CompanyAssignmentTab from "./tabs/company-assignment-settings";
import RequirementSettingsTab from "./tabs/requirement-settings/requirement-settings";
import CloudFrontSettingsTab from './tabs/cloudfront-settings';

const GeneralSettingsPage: React.FunctionComponent = () => {
  const canManageCheckList = useHasPermission(AppPermissions.ManageCheckList);
  const canUpdateGeneralSettings = useHasPermission(AppPermissions.UpdateGeneralSettings);

  const [tabName, setTabName] = React.useState<SettingsTabNames>(
    canUpdateGeneralSettings
      ? SettingsTabNames.General
      : canManageCheckList
      ? SettingsTabNames.CheckList
      : SettingsTabNames.General
  );
  const handleTabChange = (tabNameValue: SettingsTabNames) => {
    setTabName(tabNameValue);
  };

  const { data: settings, loaded, loading, reload } = useDataLoader<Settings>(() =>
    api.settings.getSettings()
  );

  return (
    <AppLayout>
      <div className="list-head border-bottom py-4 px-3">
        <h2 className="m-0 font-weight-semibold">Settings</h2>
      </div>
      {(!loaded || loading) && <ContainerLoader></ContainerLoader>}
      {settings && (
        <div className="row no-gutters ch-100">
          <LeftMenu tabChangeHandler={handleTabChange} tabName={tabName} data={settings} />
          {tabName === SettingsTabNames.General && (
            <GeneralSettingsTab
              data={{
                currency: settings.currency,
                timeZone: settings.timeZone?.timeZone!,
                franchiseName: settings.franchiseName,
                logoUrl: settings.logoUrl,
                alghanimLogoUrl: settings.alghanimLogoUrl,
                removeVisibilityGroupChildrenOnDelete:
                  settings.removeVisibilityGroupChildrenOnDelete,
              }}
              reload={reload}
            />
          )}
          {tabName === SettingsTabNames.SMS && (
            <SMSSettingsTab data={settings.sms} reload={reload} />
          )}
          {tabName === SettingsTabNames.Autoline && (
            <AutolineSettingsTab data={settings.autoline} reload={reload} />
          )}
          {tabName === SettingsTabNames.SiblingBrands && (
            <SiblingBrandsSettingsTab data={settings.siblingBrands} reload={reload} />
          )}
          {tabName === SettingsTabNames.AzureAd && (
            <AzureAdSettingsTab data={settings.azureAd} reload={reload} />
          )}
          {tabName === SettingsTabNames.Outlook && (
            <OutlookSettingsTab data={settings.outlook} reload={reload} />
          )}
          {tabName === SettingsTabNames.OneDrive && (
            <OneDriveSettingsTab data={settings.oneDrive} reload={reload} />
          )}
          {tabName === SettingsTabNames.Email && (
            <EmailSettingsTab data={settings.email} reload={reload} />
          )}
          {tabName === SettingsTabNames.Oorjit && (
            <OorjitSettingsTab data={settings.oorjit} reload={reload} />
          )}
          {tabName === SettingsTabNames.FacebookLeadsApi && (
            <FacebookLeadsApiSettingsTab data={settings.externalLeads?.fb} reload={reload} />
          )}
          {tabName === SettingsTabNames.TiktokokLeadsApi && (
            <TiktokokLeadsApiSettingsTab data={settings.externalLeads?.tiktok} reload={reload} />
          )}
          {tabName === SettingsTabNames.LinkedInLeadsApi && (
            <LinkedInLeadsApiSettingsTab data={settings.externalLeads?.linkedin} reload={reload} />
          )}
          {tabName === SettingsTabNames.Quotation && (
            <QuotationSettingsTab data={settings.quotation} reload={reload} />
          )}
          {tabName === SettingsTabNames.Booking && (
            <BookingSettingsTab data={settings.booking} reload={reload} />
          )}

          {tabName === SettingsTabNames.ActivitySettings && (
            <ActivitySettingsTab data={settings.activity} reload={reload} />
          )}
          {tabName === SettingsTabNames.PreBooking && (
            <PreBookingSettingsTab data={settings.preBooking} reload={reload} />
          )}
          {tabName === SettingsTabNames.LeadDistribution && (
            <LeadAssignmentTab data={settings.leadAssignment} reload={reload} />
          )}
          {tabName === SettingsTabNames.CompanyAssignment && (
            <CompanyAssignmentTab data={settings.companyAssignment} reload={reload} />
          )}
          {tabName === SettingsTabNames.LeadActivity && (
            <LeadActivitySettingsTab data={settings.leadActivityAssignment} reload={reload} />
          )}
          {tabName === SettingsTabNames.ShortenUrl && (
            <ShortenUrlSettingsTab data={settings.shortenUrl} reload={reload} />
          )}
          {tabName === SettingsTabNames.PDF && (
            <PDFSettingsTab data={settings.pdf} reload={reload} />
          )}
          {tabName === SettingsTabNames.Application && (
            <ApplicationSettingsTab data={settings.application} reload={reload} />
          )}
          {tabName === SettingsTabNames.CloudFront && (
            <CloudFrontSettingsTab data={settings.cloudFront} reload={reload} />
          )}
          {tabName === SettingsTabNames.SocialMediaLinks && (
            <SocialMediaLinksTab data={settings.socialMedia} reload={reload} />
          )}
          {tabName === SettingsTabNames.NotificationSettings && (
            <NotificationSettingsTab data={settings.notification} reload={reload} />
          )}
          {tabName === SettingsTabNames.AppNotificationSettings && (
            <AppNotificationSettingsTab data={settings.notification} reload={reload} />
          )}

          {tabName === SettingsTabNames.HolidaySettings && <HolidaySettingsTab />}

          {tabName === SettingsTabNames.LeadLostApprovalSettings && (
            <LeadLostApprovalSettingsTab data={settings.leadLostApproval} reload={reload} />
          )}
          {tabName === SettingsTabNames.AiApiSettings && (
            <AiApiSettingsTab data={settings.aiApiConfig} reload={reload} />
          )}
          {tabName === SettingsTabNames.FollowUpGapTime && (
            <FollowUpGapTimeSettingsTab data={settings.followUp} reload={reload} />
          )}
          {tabName === SettingsTabNames.FollowupSettings && (
            <FollowupSettingsTab data={settings.leadStageFollowupActivities} reload={reload} />
          )}
          {tabName === SettingsTabNames.StageToMustFieldMapping && <StageToMustFieldMappingTab />}
         
          {tabName === SettingsTabNames.VehicleReportSettings && (
            <VehicleReportSettingsTab data={settings.vehicleReportSettings} reload={reload} />
          )}
          {tabName === SettingsTabNames.UserbackSettings && (
            <UserbackSettingsTab data={settings.userback} reload={reload} />
          )}
          {tabName === SettingsTabNames.CheckList && <CheckListManagement />}
          {tabName === SettingsTabNames.WhatsappLeadAssignment && (
            <WhatsappLeadAssignmentTab data={settings.whatsappLeadAssignment} reload={reload} />
          )}
          {tabName === SettingsTabNames.RequirementSettings && (
            <RequirementSettingsTab data={settings?.toleranceSettings} reload={reload} />
          )}
        </div>
      )}
    </AppLayout>
  );
};

export default GeneralSettingsPage;
